<script setup lang="ts">
import { onMounted, onUnmounted, ref } from "vue"
import { useUploads } from "./Uploads"
import { useUploadDialogState } from "./uploadDialogState"
import { CloudArrowUpIcon } from "@heroicons/vue/24/outline"

const uploads = useUploads()
const dialog = useUploadDialogState()

const dragging = ref(false)
let leaveTimer: number | undefined

function hasFilesInDataTransfer(dt: DataTransfer | null | undefined): boolean {
  if (!dt) return false
  const types = Array.from(dt.types ?? [])
  // In browsers, file drags expose "Files" in types. In happy-dom (used for
  // unit tests), items are reflected as MIME types instead, so also accept
  // a non-empty items list as an indicator of file content.
  return types.includes("Files") || dt.items.length > 0 || dt.files.length > 0
}

function onDragOver(event: DragEvent): void {
  if (!hasFilesInDataTransfer(event.dataTransfer)) return
  event.preventDefault()
  dragging.value = true
  if (leaveTimer) clearTimeout(leaveTimer)
}

function onDragLeave(_event: DragEvent): void {
  leaveTimer = window.setTimeout(() => { dragging.value = false }, 100)
}

function onDrop(event: DragEvent): void {
  if (!hasFilesInDataTransfer(event.dataTransfer)) return
  event.preventDefault()
  dragging.value = false
  const files = event.dataTransfer?.files
  if (!files || !files.length) return
  for (const file of Array.from(files)) uploads.upload(file)
  dialog.open()
}

onMounted(() => {
  window.addEventListener("dragover", onDragOver)
  window.addEventListener("dragleave", onDragLeave)
  window.addEventListener("drop", onDrop)
})

onUnmounted(() => {
  window.removeEventListener("dragover", onDragOver)
  window.removeEventListener("dragleave", onDragLeave)
  window.removeEventListener("drop", onDrop)
})
</script>

<template>
  <Teleport to="body">
    <div
      v-if="dragging"
      data-testid="global-drop-overlay"
      class="fixed inset-0 z-40 bg-amber-400/20 border-4 border-dashed border-amber-500 flex items-center justify-center pointer-events-none"
    >
      <div class="flex flex-col items-center text-amber-800">
        <CloudArrowUpIcon class="w-16 h-16"/>
        <span class="mt-2 text-lg font-medium">Datei hier ablegen</span>
      </div>
    </div>
  </Teleport>
</template>
